import React, { useEffect, useRef, useState } from 'react';

export type NumericInputProps = {
  value: number;
  onCommit: (n: number) => void;
  min?: number;
  max?: number;
  /** Integer counts vs kg-style halves (0.5 step). */
  decimals?: boolean;
  className?: string;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
};

function formatDisplay(value: number, decimals: boolean): string {
  if (!Number.isFinite(value)) return '';
  if (!decimals) return String(Math.round(value));
  const q = Math.round(value * 2) / 2;
  return Number.isInteger(q) ? String(q) : q.toFixed(1);
}

function parseAndClamp(
  raw: string,
  options: { min: number; max?: number; decimals: boolean }
): number {
  const t = raw.trim().replace(',', '.');
  let n = options.decimals ? parseFloat(t) : parseInt(t, 10);
  if (t === '' || Number.isNaN(n)) {
    n = options.min;
  }
  if (n < options.min) n = options.min;
  if (options.max !== undefined && n > options.max) n = options.max;
  if (options.decimals) {
    return Math.round(n * 2) / 2;
  }
  return Math.round(n);
}

/**
 * Controlled numeric field: local string while typing; ignore parent `value` until blur (dirty flag + focus check).
 */
export function NumericInput({
  value,
  onCommit,
  min = 0,
  max,
  decimals = false,
  className,
  disabled,
  id,
  'aria-label': ariaLabel,
}: NumericInputProps) {
  const [text, setText] = useState(() => formatDisplay(value, decimals));
  const inputRef = useRef<HTMLInputElement>(null);
  /** User changed the field since last sync/commit — don't overwrite from props while editing. */
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (dirtyRef.current) return;
    if (inputRef.current && document.activeElement === inputRef.current) return;
    setText(formatDisplay(value, decimals));
  }, [value, decimals]);

  const commit = () => {
    const n = parseAndClamp(text, { min, max, decimals });
    onCommit(n);
    dirtyRef.current = false;
    setText(formatDisplay(n, decimals));
  };

  return (
    <input
      ref={inputRef}
      id={id}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      disabled={disabled}
      aria-label={ariaLabel}
      className={className}
      value={text}
      onChange={(e) => {
        dirtyRef.current = true;
        setText(e.target.value);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  );
}
